import { useEffect, useRef } from 'react';
import { useLoaderData } from '@remix-run/react';
import { type loader } from '~/routes/_index.tsx';
import { getIsSafariBrowser } from '~/services/layout/layout';
import { useResponsiveBreakpoint } from './use-responsive-breakpoint';

/**
 * Manage scroll restoration behavior during lazy loading.
 * In some cases, Safari on iOS may not restore scroll position correctly.
 * Applies only if `enableLazyLoadingOnScroll` and `isSafari` are true.
 */
export function useScrollRestorationOnLazyLoading() {
  const scrollRestorationRef = useRef<boolean>(false);
  const { defaultThemeV2Config } = useLoaderData<typeof loader>();
  const breakpoint = useResponsiveBreakpoint();

  const enableLazyLoadingOnScroll: boolean =
    defaultThemeV2Config.enable_lazy_loading_on_scroll;

  useEffect(() => {
    if (!enableLazyLoadingOnScroll) return;

    if (scrollRestorationRef.current) return;

    const isSafari: boolean = getIsSafariBrowser();

    if (!isSafari) return;

    if (breakpoint === 'unset') return;

    scrollRestorationRef.current = true;

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    requestAnimationFrame(() => {
      setTimeout(() => window.scrollTo(0, 0), 500);
    });
  }, [enableLazyLoadingOnScroll, breakpoint]);
}
